import { ConfigFieldType, FieldStatus, SelectorFieldDataImpl, SelectorOption, SelectorType } from 'adapter/in/common/form/types';
import { useEffect, useState } from 'react';
import SelectorValue from 'domain/entity/preset/field/selectorValue';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import { getInitialSelectorValues, mapSelectorValueToOption } from 'adapter/in/common/hooks/form/useBaseDashboardsApiSelector';

const getDefaultValues = (options: SelectorOption[]) => {
    const selectedValues = options.filter((option) => option.selected);
    return selectedValues.length > 0 ? selectedValues : [options[0]];
};

export const useBaseDefaultValuesSelector = <POption>(
    type: SelectorType,
    configType: ConfigFieldType,
    value: SelectorFieldDataImpl,
    shouldLoad: boolean,
    defaultOptions: SelectorValue[],
    onChange: (value: SelectorFieldDataImpl) => void
) => {
    const [options, setOptions] = useState<POption[]>();
    const shouldLoadOptions =
        value && [FieldStatus.INIT, FieldStatus.INITIAL_VALUE, FieldStatus.INVALID, FieldStatus.RESET].includes(value.status) && shouldLoad;

    useEffect(() => {
        if (shouldLoadOptions) {
            // calculate the expected options to load
            const optionsToSet = defaultOptions.map((option) => mapSelectorValueToOption(option, type));
            setOptions((optionsToSet as unknown) as POption[]);
            if (optionsToSet.length > 0) {
                switch (type) {
                    case SelectorType.PERIOD: {
                        const foundValue = optionsToSet.find((option) => option.selected);
                        // eslint-disable-next-line no-nested-ternary
                        const selectedValueToSet =
                            value.getInitialValue()[0] &&
                            (value.status === FieldStatus.INITIAL_VALUE ||
                                value.status === FieldStatus.DEFAULT_DATA ||
                                value.status === FieldStatus.RESET) &&
                            [FieldStatus.HAS_VALUE, FieldStatus.INITIAL_VALUE, FieldStatus.RESET].includes(value.status)
                                ? (value.getInitialValue() as Array<string>)[0]
                                : foundValue?.id || optionsToSet[0].id!;
                        onChange(
                            buildSelectorFieldData(FieldStatus.HAS_VALUE, value.initialValue, configType, [
                                {
                                    type: SelectorType.SELECT,
                                    value: selectedValueToSet,
                                    label: selectedValueToSet,
                                    color: selectedValueToSet,
                                    id: selectedValueToSet,
                                },
                            ])
                        );
                        break;
                    }
                    case SelectorType.FIELD_GROUP:
                    case SelectorType.SELECT:
                    default: {
                        const initialValues = getInitialSelectorValues(value, optionsToSet);
                        const selectedValueToSet = initialValues.length > 0 ? initialValues : getDefaultValues(optionsToSet);
                        onChange(
                            buildSelectorFieldData(
                                selectedValueToSet.length > 0 ? FieldStatus.HAS_VALUE : FieldStatus.INVALID,
                                selectedValueToSet.map((selValue) => selValue.value).join(','),
                                configType,
                                selectedValueToSet
                            )
                        );
                        break;
                    }
                }
            }
        }
    }, [shouldLoadOptions, defaultOptions]);

    return {
        isLoading: options?.length === 0,
        options: options && options.length > 0 ? options : [],
        maxSelectionLength: 1,
        minSelectionLength: 1,
    };
};
